import React, { useEffect, useCallback, useMemo, useState } from "react";
import axios from "axios";
import TPC_URL from "../../../utils/TPC_URL";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  Typography,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { AG_GRID_LOCALE_KR } from "@ag-grid-community/locale";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const InnerContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
`;

const ChipsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 24px;
  background-color: #f5f5f5;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const StyledFormControl = styled(FormControl)`
  min-width: 400px;
  width: 350px;
`;

const EmptySelectionMessage = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  color: #757575;
`;
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid var(--mui-palette-divider);
`;

const gridStyles = `
  .ag-theme-material .ag-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #e0e0e0;
  }
  .ag-theme-material .ag-header-cell {
    border-right: 1px solid #e0e0e0;
    background: #faf7f7;
  }
  .ag-theme-material .ag-header-cell-label {
    justify-content: center;
  }
  .changed-row {
    background-color: #ffe0e0;
  }
  .changed-row:hover {
    background-color: #fcd7d7 !important;
  }
  .ag-header-cell-text {
    font-size: 1rem;
  }
  .ag-theme-material .ag-root-wrapper {
    border: none !important;
  }
  .ag-theme-material {
    --ag-borders: none;
    --ag-row-border-width: 1px;
    --ag-row-border-color: #e0e0e0;
  }
  .ag-header-cell-resize {
    background-color: #e0e0e0;
    width: 4px;
  }
  .ag-theme-material .ag-paging-panel {
    display: none;
  }
  .ag-body-viewport::-webkit-scrollbar,
  .ag-center-cols-viewport::-webkit-scrollbar,
  .ag-center-cols-container::-webkit-scrollbar,
  .ag-body-horizontal-scroll-viewport::-webkit-scrollbar,
  .ag-body-vertical-scroll-viewport::-webkit-scrollbar,
  .ag-virtual-list-viewport::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  .ag-body-viewport::-webkit-scrollbar-track,
  .ag-center-cols-viewport::-webkit-scrollbar-track,
  .ag-center-cols-container::-webkit-scrollbar-track,
  .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-track,
  .ag-body-vertical-scroll-viewport::-webkit-scrollbar-track,
  .ag-virtual-list-viewport::-webkit-scrollbar-track {
    background: transparent;
  }
  .ag-body-viewport::-webkit-scrollbar-thumb,
  .ag-center-cols-viewport::-webkit-scrollbar-thumb,
  .ag-center-cols-container::-webkit-scrollbar-thumb,
  .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb,
  .ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb,
  .ag-virtual-list-viewport::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
`;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 350,
    },
  },
};

function ImportLog() {
  const { group_code, project_code } = useParams();
  const [logs, setLogs] = useState([]);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      suppressMenu: false,
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: "center" },
    }),
    []
  );
  const columnDefs = useMemo(
    () => [
      { headerName: "Work center", field: "work_center" },
      { headerName: "Order", field: "order_number" },
      { headerName: "Earliest start date", field: "earliest_start_date" },
      { headerName: "Material Grouping", field: "material_grouping" },
      { headerName: "Add.descr.4", field: "add_descr_4" },
      { headerName: "Material", field: "material" },
      {
        headerName: "Order item quantity",
        field: "order_item_quantity",
        valueFormatter: (params) =>
          params.value ? params.value.toLocaleString() : "",
      },
      { headerName: "Header text", field: "header_text" },
    ],
    []
  );
  useEffect(() => {
    if (rowData.length > 0) {
      setTotalPages(Math.ceil(rowData.length / pageSize));
    } else {
      setTotalPages(0);
    }
  }, [rowData, pageSize]);
  const fetchLogs = useCallback(async () => {
    setInitialLoading(true);
    try {
      const response = await axios.get(
        TPC_URL +
          `/api/tpc/getImportLog?group_code=${group_code}&project_code=${project_code}`
      );
      if (response.data && Array.isArray(response.data)) {
        setLogs(response.data);
        setSelectedLogIds([]);
      } else {
        setLogs([]);
        setError("로그 데이터 형식이 유효하지 않습니다");
      }
    } catch (err) {
      console.error("로그 데이터 로딩 오류:", err);
      setError(`로그 데이터를 불러오는 중 오류가 발생했습니다: ${err.message}`);
      setLogs([]);
    } finally {
      setInitialLoading(false);
    }
  }, [group_code, project_code]);
  const fetchRawData = useCallback(async () => {
    if (selectedLogIds.length === 0) {
      setRowData([]);
      return;
    }
    setLoading(true);
    setRowData([]);
    try {
      const promises = selectedLogIds.map((importId) =>
        axios.get(
          TPC_URL +
            `/api/tpc/getRawData?group_code=${group_code}&project_code=${project_code}&import_id=${importId}`
        )
      );
      const responses = await Promise.all(promises);
      const allData = responses.reduce((acc, response) => {
        if (response.data && Array.isArray(response.data)) {
          return [...acc, ...response.data];
        }
        return acc;
      }, []);
      setRowData(allData);
    } catch (err) {
      console.error("원시 데이터 로딩 오류:", err);
      setError(`원시 데이터를 불러오는 중 오류가 발생했습니다: ${err.message}`);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, selectedLogIds]);
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  useEffect(() => {
    fetchRawData();
  }, [fetchRawData]);
  const handleLogChange = (event) => {
    const value = event.target.value;
    setSelectedLogIds(value);
  };
  const handleDelete = (importId) => {
    setSelectedLogIds((prev) => prev.filter((id) => id !== importId));
  };
  const onGridReady = useCallback((params) => {
    params.api.sizeColumnsToFit();
  }, []);
  const getLogName = (importId) => {
    const log = logs.find((log) => log.import_id === importId);
    return log ? `${log.file_name} (${log.import_date})` : importId;
  };
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return rowData.slice(startIndex, endIndex);
  }, [rowData, currentPage, pageSize]);
  if (initialLoading) {
    return (
      <Wrapper>
        <FrameContainer>
          <LoadingContainer>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              로그 데이터를 불러오는 중...
            </Typography>
          </LoadingContainer>
        </FrameContainer>
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <style>{gridStyles}</style>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              수요 정보 업로드 이력
            </Typography>
            {/* <Tooltip
              title="도움말"
              sx={{ cursor: "pointer" }}
              onClick={() =>
                goToHelpLink(
                  "http://esehelp.cafe24.com/scheduler#h-3-ai%EC%83%9D%EC%82%B0%EA%B3%84%ED%9A%8D-%ED%8E%98%EC%9D%B4%EC%A7%80"
                )
              }
            >
              <HelpOutlineIcon />
            </Tooltip> */}
          </ToolbarLeft>
          <ToolbarRight>
            <StyledFormControl size="small">
              <InputLabel id="import-log-select-label">
                업로드 내역 선택
              </InputLabel>
              <Select
                labelId="import-log-select-label"
                id="import-log-select"
                multiple
                value={selectedLogIds}
                onChange={handleLogChange}
                input={<OutlinedInput label="업로드 내역 선택" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.length} 개 선택됨
                  </Box>
                )}
                MenuProps={MenuProps}
                disabled={logs.length === 0}
                sx={{ backgroundColor: "#f8f8f8", minWidth: "350px" }}
              >
                {logs.map((log) => (
                  <MenuItem key={log.import_id} value={log.import_id}>
                    {log.file_name} ({log.import_date})
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </ToolbarRight>
        </ToolbarContainer>
        {selectedLogIds.length > 0 && (
          <ChipsContainer>
            {selectedLogIds.map((importId) => (
              <Chip
                key={importId}
                label={getLogName(importId)}
                onDelete={() => handleDelete(importId)}
                color="primary"
                variant="outlined"
                sx={{ boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
              />
            ))}
          </ChipsContainer>
        )}

        <InnerContainer>
          {loading ? (
            <LoadingContainer>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                데이터를 불러오는 중...
              </Typography>
            </LoadingContainer>
          ) : error ? (
            <LoadingContainer>
              <Typography variant="h6" color="error">
                {error}
              </Typography>
            </LoadingContainer>
          ) : selectedLogIds.length === 0 ? (
            <EmptySelectionMessage>
              <Typography variant="h5" gutterBottom>
                상단에서 업로드 내역을 선택하세요
              </Typography>
              <Typography variant="body1" color="textSecondary">
                선택한 업로드 내역의 데이터가 여기에 표시됩니다
              </Typography>
            </EmptySelectionMessage>
          ) : rowData.length === 0 ? (
            <LoadingContainer>
              <Typography variant="h6">표시할 데이터가 없습니다</Typography>
            </LoadingContainer>
          ) : (
            <div
              className="ag-theme-material"
              style={{ height: "100%", width: "100%" }}
            >
              <AgGridReact
                rowData={currentPageData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                rowSelection="multiple"
                localeText={AG_GRID_LOCALE_KR}
                animateRows={true}
                enableCellTextSelection={true}
                suppressRowClickSelection={true}
                rowHeight={48}
                headerHeight={56}
                pagination={false}
                suppressFloatingFilter={true}
                overlayNoRowsTemplate="표시할 데이터가 없습니다"
                overlayLoadingTemplate="<span class='ag-overlay-loading-center'>데이터를 불러오는 중...</span>"
              />
            </div>
          )}
        </InnerContainer>
      </FrameContainer>
      <PaginationContainer>
        <div>
          <Typography variant="body2" color="text.secondary">
            {rowData.length > 0
              ? `총 ${rowData.length}건 중 ${
                  (currentPage - 1) * pageSize + 1
                }-${Math.min(currentPage * pageSize, rowData.length)}건 표시`
              : "표시할 데이터가 없습니다"}
          </Typography>
        </div>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 100, height: 40 }}>
            <InputLabel id="page-size-select-label">페이지 크기</InputLabel>
            <Select
              labelId="page-size-select-label"
              value={pageSize}
              label="페이지 크기"
              onChange={(e) => {
                setPageSize(e.target.value);
                setCurrentPage(1);
              }}
              sx={{ height: 40 }}
            >
              {[50, 100, 150, 200].map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              sx={{ height: 40, minWidth: 60 }}
            >
              처음
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              sx={{ height: 40, minWidth: 60 }}
            >
              이전
            </Button>
            <Typography sx={{ alignSelf: "center", mx: 1 }}>
              {`${currentPage} / ${totalPages || 1}`}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              sx={{ height: 40, minWidth: 60 }}
            >
              다음
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage(totalPages)}
              sx={{ height: 40, minWidth: 60 }}
            >
              마지막
            </Button>
          </Stack>
        </Stack>
      </PaginationContainer>
    </Wrapper>
  );
}

export default ImportLog;
